import { ApiError, backendFetch } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import { announceSessionChange } from "@softmax/web-features/session/commands";
import { maxAvatarUploadBytes } from "@/lib/upload-limits";

export type AccountProfileInput = Schemas["AccountProfileRequest"];
export type AccountProfile = Schemas["AccountProfile"];

function accountRequest<T>({
  viewerId,
  path,
  init,
}: {
  viewerId: string;
  path: string;
  init: RequestInit;
}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("X-Account-User-Id", viewerId);
  return backendFetch<T>("", path, { ...init, headers }).then(
    (response) => response,
    (error: Error) => {
      if (error instanceof ApiError && error.status === 409)
        announceSessionChange({ kind: "invalidated" });

      throw error;
    },
  );
}

export function readAccountProfile({
  viewerId,
  signal,
}: {
  viewerId: string;
  signal?: AbortSignal | undefined;
}): Promise<Schemas["AccountProfileResponse"]> {
  return accountRequest({
    viewerId,
    path: "/api/user",
    init: { signal: signal ?? null },
  });
}

export function saveAccountProfile({
  viewerId,
  profile,
}: {
  viewerId: string;
  profile: AccountProfileInput;
}): Promise<Schemas["AccountProfileSavedResponse"]> {
  return accountRequest({
    viewerId,
    path: "/api/user",
    init: {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    },
  });
}

export async function uploadAccountPhoto({
  viewerId,
  image,
}: {
  viewerId: string;
  image: File;
}): Promise<string> {
  if (image.size > maxAvatarUploadBytes)
    throw new Error("File too large. Maximum size is 5MB.");

  const form = new FormData();
  form.append("file", image);
  const response = await accountRequest<{ image: string }>({
    viewerId,
    path: "/api/user/avatar",
    init: { method: "POST", body: form },
  });
  return response.image;
}

export function removeAccountPhoto(viewerId: string): Promise<{ image: null }> {
  return accountRequest({
    viewerId,
    path: "/api/user/avatar",
    init: { method: "DELETE" },
  });
}
